import React, { useState, useEffect, useRef } from "react";
import { DayPicker, useNavigation } from "react-day-picker";
import { format } from "date-fns";
import { _getTranslation } from "../utils/helper";

const CustomDatePicker = ({ disableMonthYearDropdown, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(false);
  const datepickerRef = useRef(null);

  useEffect(() => {
    let formatted = undefined;
    const value = props.value;
    if (value) {
      formatted =
        props.mode === "range"
          ? {
            from: value.from ? new Date(value.from) : undefined,
            to: value.to ? new Date(value.to) : undefined,
          }
          : new Date(value);
    }
    setSelected(formatted);
  }, [props.value, props.mode]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        datepickerRef.current &&
        !datepickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener("click", handleOutsideClick);

    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const onSelect = (newDate) => {
    let formatted = undefined;
    if (props.mode === "range") {
      formatted = {
        from: newDate?.from ? format(newDate.from, "yyyy-MM-dd") : undefined,
        to: newDate?.to ? format(newDate.to, "yyyy-MM-dd") : undefined,
      };
    } else {
      formatted = format(newDate, "yyyy-MM-dd");
    }
    props.onChange(formatted);

    if (props.mode !== "range") {
      setIsOpen(false);
    }
  };

  const _displaySelectedDate = () => {
    let text = props.placeholder ?? _getTranslation("choose_date");

    if (selected) {
      if (props.mode === "range") {
        if (selected?.from) {
          return `${format(selected.from, "dd/MM/yyyy")}${selected.to ? ` - ${format(selected.to, "dd/MM/yyyy")}` : ""
            }`;
        }
      } else {
        return format(selected, "dd/MM/yyyy");
      }
    }

    return text;
  };

  const CalendarHeader = (props) => {
    const { goToMonth, nextMonth, previousMonth, goToDate } = useNavigation();
    const [currentDisplayMonth, setCurrentDisplayMonth] = useState(
      props.displayMonth.getMonth() + 1
    );
    const [currentDisplayYear, setCurrentDisplayYear] = useState(
      props.displayMonth.getFullYear()
    );
    const currentYear = new Date().getFullYear();

    useEffect(() => {
      setCurrentDisplayMonth(props.displayMonth.getMonth() + 1);
      setCurrentDisplayYear(props.displayMonth.getFullYear());
    }, [props.displayMonth]);

    const months = Array.from({ length: 12 }, (_, i) => {
      const monthNumber = i + 1;
      const month = new Date(`${currentYear}-${monthNumber}-01`).toLocaleString(
        "en-US",
        { month: "short" }
      );

      return {
        value: monthNumber,
        label: month,
      };
    });

    const years = Array.from(
      { length: currentYear - 1950 + 1 },
      (_, i) => 1950 + i
    ).reverse();

    const handleMonthChange = (event) => {
      const selectedMonthNumber = parseInt(event.target.value, 10);
      goToDate(new Date(`${currentDisplayYear}-${selectedMonthNumber}-01`));
    };

    const handleYearChange = (event) => {
      const selectedYear = parseInt(event.target.value, 10);
      goToDate(new Date(`${selectedYear}-${currentDisplayMonth}-01`));
    };

    return (
      <div className="rdp-caption">
        <button
          name="previous-month"
          aria-label="Go to previous month"
          className="rdp-nav_button rdp-nav_button_previous"
          type="button"
          disabled={!previousMonth}
          onClick={() => previousMonth && goToMonth(previousMonth)}
        >
          {_getTranslation("previous")}
        </button>
        <div
          className={`rdp-caption_label ${!disableMonthYearDropdown ? "month-year-dropdown" : ""
            }`}
          aria-live="polite"
          role="presentation"
        >
          {!disableMonthYearDropdown ? (
            <React.Fragment>
              <select id="month" onChange={handleMonthChange}>
                {months.map((month) => (
                  <option
                    key={month.value}
                    value={month.value}
                    selected={month.value === currentDisplayMonth}
                  >
                    {month.label}
                  </option>
                ))}
              </select>
              <select id="year" onChange={handleYearChange}>
                {years.map((year) => (
                  <option
                    key={year}
                    value={year}
                    selected={year === currentDisplayYear}
                  >
                    {year}
                  </option>
                ))}
              </select>
            </React.Fragment>
          ) : (
            format(props.displayMonth, "MMM yyy")
          )}
        </div>

        <button
          name="next-month"
          aria-label="Go to next month"
          className="rdp-nav_button rdp-nav_button_next"
          type="button"
          disabled={!nextMonth}
          onClick={() => nextMonth && goToMonth(nextMonth)}
        >
          {_getTranslation("next")}
        </button>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div
        className={`${props.isNoIcon ? "datepicker-container-noicon" : "datepicker-container"} ${isOpen ? "active" : ""} ${props.class ?? ""}`}
        ref={datepickerRef}
      >
        <button
          className={`datepicker-toggle ${isOpen ? "active" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {/* <span className="datepicker-toggle-icon datepicker-toggle-icon__calendar"></span> */}
          {!props.isNoIcon && (
            <span className="datepicker-toggle-icon datepicker-toggle-icon__calendar"></span>
          )}
          <span className="datepicker-toggle-text">
            {_displaySelectedDate()}
          </span>
          <span className="datepicker-toggle-icon datepicker-toggle-icon__arrow"></span>
        </button>
        {isOpen && (
          <DayPicker
            mode={props.mode ?? "single"}
            selected={selected}
            defaultMonth={
              props.mode === "range" ? selected?.from ?? new Date() : selected
            }
            onSelect={onSelect}
            components={{
              Caption: CalendarHeader,
            }}

            onDayMouseEnter={(_, __, event) => {
              if (selected === undefined) { return; }

              // if more then 1 selected then no need of hover effect
              if ((selected.from && selected.to) || (!selected.from && !selected.to)) return;

              // Get the current target button
              const currentButton = event.target;

              // Find the closest ancestor <tbody> element
              const tbody = currentButton.closest('tbody');

              // Find all buttons within the <tbody> element
              const buttons = Array.from(tbody.querySelectorAll('button'));

              // Find the index of the current button in the buttons array
              const currentIndex = buttons.indexOf(currentButton);

              // Find the index of the button with aria-selected="true"
              const selectedIndex = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');

              // Create an array to store the selected buttons
              const selectedButtons = [];

              // Determine the range of buttons to select
              const start = Math.min(currentIndex, selectedIndex);
              const end = Math.max(currentIndex, selectedIndex);

              // Select the buttons between the current button and the button with aria-selected="true" (inclusive)
              // eslint-disable-next-line no-plusplus
              for (let i = start; i <= end; i++) {
                selectedButtons.push(buttons[i]);
              }

              buttons.forEach((item) => {
                // run over all buttons and set data-hover true to those who in range
                item.setAttribute('data-hover', selectedButtons.includes(item) ? 'true' : 'false');
              });
            }}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default CustomDatePicker;
